import { useState, useEffect } from 'react'
import {
  X,
  ExternalLink,
  DollarSign,
  Clock,
  Calendar,
  Activity,
  FileText,
  Lock,
  CheckCircle,
  Send,
} from 'react-feather'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import {
  useFetchComplianceExplainQuery,
  useAskCompliancePermissionMutation,
} from '../findoraDashboardListApiSlice'

const formatIndonesianDate = (dateStr) => {
  if (!dateStr) return '-'
  const shortMonths = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'Mei',
    'Jun',
    'Jul',
    'Agt',
    'Sep',
    'Okt',
    'Nov',
    'Des',
  ]
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return String(dateStr)
  return `${d.getDate()} ${shortMonths[d.getMonth()]} ${d.getFullYear()}`
}

const formatExplanationNumbers = (text, variables = []) => {
  if (!text || typeof text !== 'string') return text

  let formatted = text

  // 1. Synchronize raw numbers using variables from API response
  if (Array.isArray(variables) && variables.length > 0) {
    variables.forEach((v) => {
      if (v && v.value !== null && v.value !== undefined) {
        const rawStr = String(v.value)
        if (typeof v.value === 'number' || (!isNaN(Number(v.value)) && v.value !== '')) {
          const num = typeof v.value === 'number' ? v.value : parseFloat(v.value)

          if (v.unit === 'Rp') {
            // Currency amounts: show normally as integer Rupiah (e.g. Rp 5.000.000)
            const formattedMoney = formatCurrencyDisplay(num)
            const escaped = rawStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            const regexRp = new RegExp(`Rp\\s*${escaped}\\b`, 'g')
            formatted = formatted.replace(regexRp, formattedMoney)
          } else if (!Number.isInteger(num)) {
            // Non-money float values (e.g. standard_monthly_hours = 58.666666666666664): format to max 2 decimals
            const formattedFloat = newFormatThousandSeparator(num, 2)
            const escaped = rawStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            const regexFloat = new RegExp(`\\b${escaped}\\b`, 'g')
            formatted = formatted.replace(regexFloat, formattedFloat)
          }
        }
      }
    })
  }

  // 2. Format unformatted Rp amounts: e.g. "Rp 5000000" -> "Rp 5.000.000", "Rp 85227/jam" -> "Rp 85.227/jam", "Rp 85227" -> "Rp 85.227"
  // Money amounts should ALWAYS be shown normally as whole Rupiah with Indonesian thousand separators!
  formatted = formatted.replace(/Rp\s*(\d{4,})(?:\.(\d+))?(\/[a-zA-Z]+)?/g, (match, intPart, decPart, unitPart) => {
    const num = parseInt(intPart, 10)
    if (isNaN(num)) return match
    const formattedNum = newFormatThousandSeparator(num, 0)
    return `Rp ${formattedNum}${unitPart || ''}`
  })

  // 3. Format computer floats with 4 or more decimal digits: e.g. "58.666666666666664" -> "58,67"
  // Do NOT match Indonesian thousand groups (e.g. 85.227 or 5.000.000) which only have exactly 3 digits after dot!
  formatted = formatted.replace(/\b(\d+)\.(\d{4,})\b/g, (match, integerPart, decimalPart) => {
    const num = parseFloat(`${integerPart}.${decimalPart}`)
    if (isNaN(num)) return match
    return newFormatThousandSeparator(num, 2)
  })

  return formatted
}

const renderHighlightedExplanation = (text, variables = [], boldClassName = 'fw-bold text-dark') => {
  if (!text || typeof text !== 'string') return text

  const formattedText = formatExplanationNumbers(text, variables)

  // Regex matches:
  // - Currencies: Rp 5.000.000, Rp 0/jam, Rp 85.227, etc.
  // - Percentages: 100%, 0%
  // - Quantities with units: 58,67 jam, 0 jam, 8 jam/hari, 10 hari, 600 item, 12 catatan
  // - Numbers: standalone digits
  const regex =
    /(Rp\s*\d+(?:\.\d{3})*(?:,\d+)?(?:\/[a-zA-Z]+)?|\d+(?:,\d+)?%|\d+(?:,\d+)?\s*(?:jam(?:\/hari)?|hari|item|catatan|item\/jam)|\b\d+(?:,\d+)?\b)/g

  const parts = []
  let lastIndex = 0
  let match

  while ((match = regex.exec(formattedText)) !== null) {
    const matchIndex = match.index
    const matchStr = match[0]

    // Skip 4-digit years following month names (e.g. "September 2026", "Feb 1985")
    const precedingText = formattedText.slice(Math.max(0, matchIndex - 15), matchIndex)
    const isYear =
      /^(19|20)\d{2}$/.test(matchStr) &&
      /(Januari|Februari|Maret|April|Mei|Juni|Juli|Agustus|September|Oktober|November|Desember|Jan|Feb|Mar|Apr|Mei|Jun|Jul|Agt|Sep|Okt|Nov|Des)\s+$/i.test(
        precedingText
      )

    // Skip date days followed by month names (e.g. "26 Feb", "3 Nov")
    const followingText = formattedText.slice(regex.lastIndex, regex.lastIndex + 15)
    const isDateDay =
      /^\d{1,2}$/.test(matchStr) &&
      /^\s+(Januari|Februari|Maret|April|Mei|Juni|Juli|Agustus|September|Oktober|November|Desember|Jan|Feb|Mar|Apr|Mei|Jun|Jul|Agt|Sep|Okt|Nov|Des)\b/i.test(
        followingText
      )

    if (matchIndex > lastIndex) {
      parts.push(formattedText.slice(lastIndex, matchIndex))
    }

    if (isYear || isDateDay) {
      parts.push(matchStr)
    } else {
      parts.push(
        <strong key={matchIndex} className={boldClassName}>
          {matchStr}
        </strong>
      )
    }

    lastIndex = regex.lastIndex
  }

  if (lastIndex < formattedText.length) {
    parts.push(formattedText.slice(lastIndex))
  }

  return parts
}

const formatCurrencyDisplay = (val) => {
  if (val === null || val === undefined || val === 'N/A' || val === '') {
    return 'N/A'
  }
  const cleanVal = String(val).replace(/^Rp\s*/i, '').replace(/\./g, '').replace(/,/g, '.')
  const num = typeof val === 'number' ? val : parseFloat(cleanVal)
  if (isNaN(num)) return String(val)
  const maxDecimals = num % 1 !== 0 ? 2 : 0
  return `Rp ${newFormatThousandSeparator(num, maxDecimals)}`
}

const METRIC_CONFIG = {
  contract: {
    title: 'Kontrak Kerja',
    category: 'onboarding',
  },
  insurance: {
    title: 'Jaminan Sosial & Asuransi',
    category: 'onboarding',
  },
  minimumAge: {
    title: 'Kepatuhan Usia Minimum',
    category: 'onboarding',
  },
  wageAboveMinimum: {
    title: 'Kesesuaian Upah Minimum',
    category: 'wage',
  },
  workHours: {
    title: 'Jam Kerja',
    category: 'wage',
  },
  paymentLogs: {
    title: 'Log Pembayaran Upah',
    category: 'wage',
  },
  productivity: {
    title: 'Produktivitas Kerja',
    category: 'wage',
  },
  noOvertime: {
    title: 'Bebas Lembur Berlebih',
    category: 'wage',
  },
  capacity: {
    title: 'Kapasitas Kerja',
    category: 'wage',
  },
}

const getVariableIcon = (key) => {
  const k = (key || '').toLowerCase()
  const iconProps = {
    size: 14,
    style: { marginRight: '8px' },
  }
  if (
    k.includes('wage') ||
    k.includes('payment') ||
    k.includes('fee') ||
    k.includes('ump') ||
    k.includes('upah') ||
    k.includes('pembayaran')
  ) {
    return <DollarSign {...iconProps} className="text-success flex-shrink-0" />
  }
  if (
    k.includes('hour') ||
    k.includes('jam') ||
    k.includes('secs') ||
    k.includes('overtime') ||
    k.includes('lembur') ||
    k.includes('threshold')
  ) {
    return <Clock {...iconProps} className="text-primary flex-shrink-0" />
  }
  if (
    k.includes('date') ||
    k.includes('birth') ||
    k.includes('start') ||
    k.includes('lahir') ||
    k.includes('tanggal')
  ) {
    return <Calendar {...iconProps} className="text-info flex-shrink-0" />
  }
  if (
    k.includes('contract') ||
    k.includes('kontrak') ||
    k.includes('doc') ||
    k.includes('img') ||
    k.includes('report') ||
    k.includes('laporan') ||
    k.includes('row')
  ) {
    return <FileText {...iconProps} className="text-warning flex-shrink-0" />
  }
  return <Activity {...iconProps} className="text-secondary flex-shrink-0" />
}

const ComplianceDetailDrawer = ({
  selectedMetric,
  selectedMonthYear,
  fromDate,
  toDate,
  requireDetailPermission: propRequireDetailPermission,
  requireDetailPermissionName: propRequireDetailPermissionName,
  onClose,
  onSelectMetric,
}) => {
  const {
    metricKey,
    metricLabel = 'Metric',
    value,
    category = 'wage',
    regionName = '-',
    supplierName,
    entityName = '-',
    entityType = 'Member',
    entity = {},
  } = selectedMetric || {}

  const detailData = entity.detailData || {}
  const metadata = entity.metadata || {}
  const detailPercentages = detailData.percentages || {}

  const hasMemberDetail = Boolean(
    selectedMetric &&
    entityType === 'Member' &&
    detailData &&
    (detailData.userId || detailData.name) &&
    metricKey
  )

  const {
    data: explainResult,
    isLoading: isExplainLoading,
    isFetching: isExplainFetching,
  } = useFetchComplianceExplainQuery(
    {
      scope: 'member',
      field: metricKey,
      detailData,
    },
    {
      skip: !hasMemberDetail,
    }
  )

  const rawRequirePermission =
    propRequireDetailPermission ??
    selectedMetric?.requireDetailPermission ??
    selectedMetric?.entity?.requireDetailPermission ??
    detailData?.requireDetailPermission ??
    explainResult?.requireDetailPermission ??
    explainResult?.data?.requireDetailPermission

  const requireDetailPermission =
    rawRequirePermission === true ||
    rawRequirePermission === 'true' ||
    rawRequirePermission === 1 ||
    rawRequirePermission === '1'

  const requireDetailPermissionName =
    propRequireDetailPermissionName ||
    selectedMetric?.requireDetailPermissionName ||
    selectedMetric?.entity?.requireDetailPermissionName ||
    detailData?.requireDetailPermissionName ||
    explainResult?.requireDetailPermissionName ||
    explainResult?.data?.requireDetailPermissionName ||
    ''

  const [askPermission, { isLoading: isAskingPermission }] =
    useAskCompliancePermissionMutation()

  const [permissionRequestStatus, setPermissionRequestStatus] = useState('idle')
  const [permissionErrorMessage, setPermissionErrorMessage] = useState('')

  useEffect(() => {
    setPermissionRequestStatus('idle')
    setPermissionErrorMessage('')
  }, [detailData?.userId, metricKey])

  const handleRequestPermission = async () => {
    try {
      setPermissionErrorMessage('')
      await askPermission().unwrap()
      setPermissionRequestStatus('success')
    } catch (err) {
      setPermissionRequestStatus('error')
      setPermissionErrorMessage(
        err?.data?.message ||
          err?.message ||
          'Gagal mengajukan permohonan izin akses. Silakan coba lagi.'
      )
    }
  }

  if (!selectedMetric) return null

  const apiExplanationId = (() => {
    if (!explainResult?.explanation) return null
    if (typeof explainResult.explanation === 'string') return explainResult.explanation.trim()
    return (explainResult.explanation.id || explainResult.explanation.en || '').trim()
  })()

  const apiInsightId = (() => {
    if (!explainResult?.insight) return null
    if (typeof explainResult.insight === 'string') return explainResult.insight.trim()
    return (explainResult.insight.id || explainResult.insight.en || '').trim()
  })()

  const apiVariables = Array.isArray(explainResult?.variables)
    ? explainResult.variables
    : []

  const isWage = category === 'wage'

  const wageData = {
    workHours: detailPercentages.workHours ?? entity.wage?.workHours ?? null,
    noOvertime: detailPercentages.noOvertime ?? entity.wage?.noOvertime ?? null,
    wageAboveMinimum:
      detailPercentages.wageAboveMinimum ?? entity.wage?.wageAboveMinimum ?? null,
    paymentLogs: detailPercentages.paymentLogs ?? entity.wage?.paymentLogs ?? null,
    productivity: detailPercentages.productivity ?? entity.wage?.productivity ?? null,
    capacity: detailPercentages.capacity ?? entity.wage?.capacity ?? null,
  }

  const onboardingData = {
    contract: detailPercentages.contract ?? entity.onboarding?.contract ?? null,
    insurance: detailPercentages.insurance ?? entity.onboarding?.insurance ?? null,
    minimumAge: detailPercentages.minimumAge ?? entity.onboarding?.minimumAge ?? null,
  }

  const currentMetricVal = isWage ? wageData[metricKey] : onboardingData[metricKey]
  const effectiveValue =
    currentMetricVal !== undefined && currentMetricVal !== null
      ? currentMetricVal
      : value

  const hasMetricValue =
    effectiveValue !== null &&
    effectiveValue !== undefined &&
    effectiveValue !== 'N/A' &&
    effectiveValue !== ''

  const numericValue = hasMetricValue
    ? typeof effectiveValue === 'number'
      ? effectiveValue
      : parseFloat(String(effectiveValue).replace(/,/g, ''))
    : null

  // Period label formatted cleanly
  const cleanPeriodLabel = (() => {
    if (detailData.monthLabel) {
      if (
        detailData.monthLabelEn &&
        detailData.monthLabelEn.toLowerCase() !== detailData.monthLabel.toLowerCase()
      ) {
        return `${detailData.monthLabel} (${detailData.monthLabelEn})`
      }
      return detailData.monthLabel
    }
    return selectedMonthYear || '-'
  })()

  const workArea = metadata.work_area || entity.alamat || '-'
  const contractDocUrl = metadata.contractImgs || detailData.contractImgs

  // Formulated explanation directly using API response or real data fallback
  const explanationText = (() => {
    if (apiExplanationId) {
      return apiExplanationId
    }
    if (metricKey === 'wageAboveMinimum' && apiVariables.length > 0) {
      const getVar = (needle, fallback = '0') => {
        const item = apiVariables.find(
          (v) =>
            v.key?.toLowerCase().includes(needle.toLowerCase()) ||
            v.label?.id?.toLowerCase().includes(needle.toLowerCase())
        )
        if (!item) return fallback
        if (item.unit === 'Rp') return formatCurrencyDisplay(item.value)
        if (item.unit) return `${newFormatThousandSeparator(item.value, 2)} ${item.unit}`
        return typeof item.value === 'number'
          ? newFormatThousandSeparator(item.value, 2)
          : String(item.value ?? fallback)
      }
      const lapBayar = getVar('laporan pembayaran', '0')
      const totBayar = getVar('total pembayaran', 'Rp 0')
      const lapAbsen = getVar('jumlah laporan', '0')
      const totJam = getVar('total jam kerja', '0 jam')
      const upahPerJam = getVar('upah per jam', 'Rp 0')
      const upahMin = getVar('upah minimum', 'Rp 0')
      const stdJam = getVar('standar jam bulanan', '0 jam')

      const rawUpahMinItem = apiVariables.find(
        (v) => v.key?.toLowerCase().includes('minimum') || v.label?.id?.toLowerCase().includes('minimum')
      )
      const rawStdJamItem = apiVariables.find(
        (v) => v.key?.toLowerCase().includes('standard') || v.label?.id?.toLowerCase().includes('standar')
      )
      const upahMinNum = rawUpahMinItem ? parseFloat(rawUpahMinItem.value) : 0
      const stdJamNum = rawStdJamItem ? parseFloat(rawStdJamItem.value) : 0
      const umpPerJamNum = stdJamNum > 0 ? upahMinNum / stdJamNum : 0
      const umpPerJamStr = `Rp ${newFormatThousandSeparator(Math.round(umpPerJamNum), 0)}`
      const formattedHourlyWage = upahPerJam.endsWith('/jam') ? upahPerJam : `${upahPerJam}/jam`

      return `Dari ${lapBayar} laporan pembayaran, ${entityName} menerima ${totBayar} di ${cleanPeriodLabel}. Dari ${lapAbsen} laporan absensi, ${entityName} bekerja total ${totJam}. Dari sini dihitung upah per jam ${entityName} adalah ${totBayar} dibagi ${totJam}, sebesar ${formattedHourlyWage}. UMP adalah ${upahMin} per bulan. Standar jam kerja adalah ${stdJam}. UMP per jam berarti ${umpPerJamStr}/jam. Persentase dihitung dari upah per jam ${entityName} ${upahPerJam} dibagi UMP per jam ${umpPerJamStr}, kali 100%. Didapat ${newFormatThousandSeparator(numericValue, 2)}%.`
    }
    if (metricKey === 'noOvertime') {
      const otDays = detailData.totalOvertimeDays ?? 0
      const threshold = detailData.overtime_hour_threshold || 8
      if (numericValue >= 100) {
        return `${entityName} tidak memiliki jam lembur yang melebihi batas ketentuan (${threshold} jam/hari) pada periode ${cleanPeriodLabel}. Tingkat kepatuhan tercatat 100%.`
      }
      return `${entityName} tercatat memiliki ${otDays} hari lembur yang melampaui batas ketentuan (${threshold} jam/hari) pada periode ${cleanPeriodLabel}.`
    }
    if (metricKey === 'capacity') {
      return `Pencapaian kapasitas kerja ${entityName} pada periode ${cleanPeriodLabel} tercatat sebesar ${hasMetricValue ? `${newFormatThousandSeparator(numericValue, 2)}%` : 'N/A'}.`
    }
    if (hasMetricValue) {
      return `Tingkat kepatuhan ${metricLabel} untuk ${entityName} pada periode ${cleanPeriodLabel} tercatat sebesar ${newFormatThousandSeparator(numericValue, 2)}% berdasarkan hasil verifikasi data operasional.`
    }
    return `Data kepatuhan untuk indikator ${metricLabel} belum tercatat (N/A) pada periode ${cleanPeriodLabel}.`
  })()

  const activeConfig = METRIC_CONFIG[metricKey] || {
    title: metricLabel,
    category,
  }

  return (
    <div
      className="bg-white d-flex flex-column h-100"
      style={{
        width: '100%',
        minWidth: '340px',
        overflowY: 'auto',
      }}
      data-testid="compliance-detail-drawer"
    >
      {/* 1. HEADER: WORKER IDENTITY & CLOSE BUTTON */}
      <div className="p-4 pb-3 border-bottom d-flex justify-content-between align-items-start">
        <div className="d-flex align-items-center gap-3">
          <div
            className="rounded-circle text-white fw-bold d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: '40px',
              height: '40px',
              fontSize: '15px',
              background: 'linear-gradient(135deg, #006386 0%, #0479a4 100%)',
            }}
          >
            {entityName.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="d-flex align-items-center gap-2">
              <h5 className="fw-bold text-dark mb-0" style={{ fontSize: '16px' }}>
                {entityName}
              </h5>
              {detailData.userId && (
                <span
                  className="badge rounded-pill fw-normal"
                  style={{
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                    fontSize: '11px',
                    padding: '2px 8px',
                  }}
                >
                  ID #{detailData.userId}
                </span>
              )}
            </div>
            <div className="text-muted mt-0.5" style={{ fontSize: '12px' }}>
              {supplierName && supplierName !== '-' ? supplierName : regionName}
              {regionName && supplierName && supplierName !== '-' && regionName !== '-' ? ` • ${regionName}` : ''}
              {workArea && workArea !== '-' ? ` • ${workArea}` : ''}
            </div>
          </div>
        </div>

        <button
          type="button"
          className="btn btn-sm btn-light rounded-circle p-1.5 text-muted border-0 d-flex align-items-center justify-content-center"
          onClick={onClose}
          aria-label="Tutup"
          style={{ width: '32px', height: '32px', cursor: 'pointer' }}
          data-testid="compliance-detail-close-btn"
        >
          <X size={18} />
        </button>
      </div>

      {/* 2. BODY CONTENT */}
      <div className="p-4 d-flex flex-column gap-4 flex-grow-1">
        {/* METRIC HEADER & STATUS */}
        <div>
          <div
            className="text-muted fw-semibold mb-1"
            style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px' }}
          >
            {isWage ? 'Indikator Upah dan Produktivitas' : 'Indikator Onboarding'}
          </div>
          <h4 className="fw-bold text-dark mb-2" style={{ fontSize: '18px' }}>
            {activeConfig.title}
          </h4>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="text-muted" style={{ fontSize: '12px' }}>
              Periode: <strong>{cleanPeriodLabel}</strong>
            </span>
          </div>
        </div>

        {/* Quick Insights - hidden */}
        {false && apiInsightId && (
          <div>
            <div
              className="fw-bold text-dark mb-2 d-flex align-items-center justify-content-between"
              style={{ fontSize: '13px' }}
            >
              <span>Quick Insights</span>
            </div>

            <div
              className="p-3 rounded-3 d-flex align-items-start gap-2"
              style={{
                backgroundColor:
                  numericValue !== null && numericValue < 70
                    ? '#fdf0f0'
                    : numericValue !== null && numericValue < 100
                      ? '#fff7e8'
                      : '#edf8f1',
                border: `1px solid ${
                  numericValue !== null && numericValue < 70
                    ? '#f1d2d2'
                    : numericValue !== null && numericValue < 100
                      ? '#f0dfb5'
                      : '#d2eadd'
                }`,
              }}
            >
              <div
                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: '20px',
                  height: '20px',
                  border: `1.5px solid ${
                    numericValue !== null && numericValue < 70
                      ? '#b14444'
                      : numericValue !== null && numericValue < 100
                        ? '#92600f'
                        : '#25734f'
                  }`,
                  color:
                    numericValue !== null && numericValue < 70
                      ? '#b14444'
                      : numericValue !== null && numericValue < 100
                        ? '#92600f'
                        : '#25734f',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  marginTop: '1px',
                }}
              >
                {numericValue !== null && numericValue < 100 ? '!' : '✓'}
              </div>
              <div>
                <p
                  className="mb-0"
                  style={{
                    fontSize: '13px',
                    lineHeight: '1.6',
                    color:
                      numericValue !== null && numericValue < 70
                        ? '#b14444'
                        : numericValue !== null && numericValue < 100
                          ? '#92600f'
                          : '#25734f',
                    fontWeight: 500,
                  }}
                >
                  {renderHighlightedExplanation(apiInsightId, apiVariables, 'fw-bold')}
                </p>
              </div>
            </div>
          </div>
        )}

        {requireDetailPermission ? (
          <div
            className="p-4 rounded-3 text-center d-flex flex-column align-items-center justify-content-center"
            style={{
              backgroundColor: '#f8fafc',
              border: '1px dashed #cbd5e1',
            }}
          >
            <div
              className="rounded-circle d-flex align-items-center justify-content-center mb-3"
              style={{
                width: '48px',
                height: '48px',
                backgroundColor:
                  permissionRequestStatus === 'success' ? '#edf8f1' : '#f1f5f9',
                color:
                  permissionRequestStatus === 'success' ? '#25734f' : '#006386',
              }}
            >
              {permissionRequestStatus === 'success' ? (
                <CheckCircle size={24} />
              ) : (
                <Lock size={22} />
              )}
            </div>

            <div
              className="fw-bold text-dark mb-1"
              style={{ fontSize: '14px' }}
            >
              {permissionRequestStatus === 'success'
                ? 'Permohonan Izin Berhasil Dikirim'
                : 'Izin Akses Diperlukan'}
            </div>

            <p
              className="text-muted mb-3"
              style={{ fontSize: '13px', lineHeight: '1.6', maxWidth: '340px' }}
            >
              {permissionRequestStatus === 'success' ? (
                <span>
                  Permohonan izin akses telah berhasil diajukan kepada{' '}
                  <strong className="text-dark">
                    {requireDetailPermissionName || 'pemilik laporan'}
                  </strong>
                  . Pemilik laporan telah menerima notifikasi permohonan Anda.
                </span>
              ) : (
                <span>
                  Anda memerlukan izin untuk melihat penjelasan dan rincian data metrik ini. Silakan ajukan permohonan akses kepada{' '}
                  <strong className="text-dark">
                    {requireDetailPermissionName || 'pemilik laporan'}
                  </strong>
                  .
                </span>
              )}
            </p>

            {permissionRequestStatus === 'error' && (
              <div
                className="alert alert-danger py-2 px-3 mb-3 text-start w-100"
                style={{ fontSize: '12.5px' }}
              >
                {permissionErrorMessage}
              </div>
            )}

            {permissionRequestStatus === 'success' ? (
              <div
                className="d-inline-flex align-items-center gap-2 py-2 px-3 rounded-2"
                style={{
                  backgroundColor: '#edf8f1',
                  color: '#25734f',
                  border: '1px solid #d2eadd',
                  fontSize: '12.5px',
                  fontWeight: 600,
                }}
              >
                <CheckCircle size={15} />
                <span>Permintaan Terkirim</span>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn-primary d-inline-flex align-items-center gap-2 px-3.5 py-2 rounded-2"
                style={{
                  backgroundColor: '#006386',
                  borderColor: '#006386',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: isAskingPermission ? 'not-allowed' : 'pointer',
                }}
                disabled={isAskingPermission}
                onClick={handleRequestPermission}
              >
                {isAskingPermission ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                      style={{ width: '13px', height: '13px' }}
                    />
                    <span>Mengajukan Permohonan...</span>
                  </>
                ) : (
                  <>
                    <Send size={14} />
                    <span>Minta Izin Akses</span>
                  </>
                )}
              </button>
            )}
          </div>
        ) : isExplainLoading || isExplainFetching ? (
          <div
            className="p-4 rounded-3 d-flex align-items-center justify-content-center gap-2 text-muted"
            style={{
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              fontSize: '13px',
            }}
          >
            <div
              className="spinner-border spinner-border-sm text-primary"
              role="status"
              style={{ width: '15px', height: '15px' }}
            />
            <span>Memuat rincian penjelasan...</span>
          </div>
        ) : (
          <>
            {/* PENJELASAN KEPATUHAN */}
            <div>
              <div className="fw-bold text-dark mb-2" style={{ fontSize: '13px' }}>
                Penjelasan
              </div>

              <div
                className="p-2 rounded-3"
                style={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                }}
              >
                <p
                  className="mb-0 text-dark"
                  style={{ fontSize: '13px', lineHeight: '1.65', color: '#1e293b' }}
                >
                  {renderHighlightedExplanation(explanationText, apiVariables, 'fw-bold text-dark')}
                </p>
              </div>
            </div>

            <div>
              <div className="fw-bold text-dark mb-2" style={{ fontSize: '13px' }}>
                Rincian Data
              </div>

              <div
                className="rounded-3 overflow-hidden bg-white"
                style={{
                  border: '1px solid #e2e8f0',
                }}
              >
                {apiVariables.length > 0 ? (
                  apiVariables.map((v, index) => {
                    const label = v.label?.id || v.key
                    let displayVal = v.value
                    const isBoolean = typeof v.value === 'boolean'
                    if (isBoolean) {
                      displayVal = v.value ? 'Terpenuhi (Ya)' : 'Belum Terpenuhi'
                    } else if (typeof v.value === 'object' && v.value !== null) {
                      displayVal = v.value.id || JSON.stringify(v.value)
                    } else if (typeof v.value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v.value)) {
                      displayVal = formatIndonesianDate(v.value)
                    } else if (
                      typeof v.value === 'number' ||
                      (typeof v.value === 'string' && !isNaN(Number(v.value)) && v.value.trim() !== '')
                    ) {
                      const num = typeof v.value === 'number' ? v.value : parseFloat(v.value)
                      displayVal = newFormatThousandSeparator(num, 2)
                    }

                    if (v.unit === 'Rp' && (typeof v.value === 'number' || !isNaN(Number(v.value)))) {
                      displayVal = formatCurrencyDisplay(v.value)
                    } else if (v.unit && displayVal !== undefined && displayVal !== null && !isBoolean) {
                      displayVal = `${displayVal} ${v.unit}`
                    }

                    const isUrl = typeof v.value === 'string' && v.value.startsWith('http')
                    const isLast = index === apiVariables.length - 1

                    return (
                      <div
                        key={v.key || index}
                        className="d-flex align-items-center justify-content-between p-3"
                        style={{
                          borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
                        }}
                      >
                        <div
                          className="d-flex align-items-center gap-2.5 text-muted"
                          style={{ fontSize: '12.5px' }}
                          title={label}
                        >
                          {getVariableIcon(v.key)}
                          <span className="text-secondary">{label}</span>
                        </div>
                        <div
                          className="fw-semibold text-dark text-end ms-2"
                          style={{ fontSize: '13px' }}
                        >
                          {isUrl ? (
                            <a
                              href={v.value}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1.5 py-1 px-2.5 text-decoration-none rounded-2"
                              style={{ fontSize: '11.5px' }}
                            >
                              <span>Buka Dokumen</span>
                              <ExternalLink size={12} />
                            </a>
                          ) : isBoolean ? (
                            <span
                              className="badge rounded-pill fw-medium"
                              style={{
                                fontSize: '11.5px',
                                padding: '3px 8px',
                                backgroundColor: v.value ? '#dcfce7' : '#fee2e2',
                                color: v.value ? '#15803d' : '#b91c1c',
                              }}
                            >
                              {displayVal}
                            </span>
                          ) : (
                            <span>{String(displayVal ?? '-')}</span>
                          )}
                        </div>
                      </div>
                    )
                  })
                ) : (
                  // Fallback rows for metrics with no variables in explain API (e.g. noOvertime, capacity)
                  metricKey === 'noOvertime' ? (
                    <>
                      <div
                        className="d-flex align-items-center justify-content-between p-3 border-bottom"
                        style={{ borderColor: '#f1f5f9' }}
                      >
                        <div className="d-flex align-items-center text-muted" style={{ fontSize: '12.5px' }}>
                          <Clock size={14} className="text-primary flex-shrink-0" style={{ marginRight: '8px' }} />
                          <span className="text-secondary">Hari Lembur Tercatat</span>
                        </div>
                        <div className="fw-semibold text-dark" style={{ fontSize: '13px' }}>
                          {detailData.totalOvertimeDays ?? 0} hari
                        </div>
                      </div>
                      <div className="d-flex align-items-center justify-content-between p-3">
                        <div className="d-flex align-items-center text-muted" style={{ fontSize: '12.5px' }}>
                          <Clock size={14} className="text-primary flex-shrink-0" style={{ marginRight: '8px' }} />
                          <span className="text-secondary">Batas Acuan Standar</span>
                        </div>
                        <div className="fw-semibold text-dark" style={{ fontSize: '13px' }}>
                          Maks {detailData.overtime_hour_threshold || 8} jam/hari
                        </div>
                      </div>
                    </>
                  ) : metricKey === 'capacity' ? (
                    <div className="d-flex align-items-center justify-content-between p-3">
                      <div className="d-flex align-items-center text-muted" style={{ fontSize: '12.5px' }}>
                        <Activity size={14} className="text-secondary flex-shrink-0" style={{ marginRight: '8px' }} />
                        <span className="text-secondary">Capaian Kapasitas</span>
                      </div>
                      <div className="fw-semibold text-dark" style={{ fontSize: '13px' }}>
                        {wageData.capacity !== null && wageData.capacity !== undefined ? `${wageData.capacity}%` : 'N/A'}
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center justify-content-between p-3">
                      <div className="text-muted" style={{ fontSize: '12.5px' }}>Nilai Indikator</div>
                      <div className="fw-semibold text-dark" style={{ fontSize: '13px' }}>
                        {hasMetricValue ? `${newFormatThousandSeparator(numericValue, 2)}%` : 'N/A'}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </>
        )}

        {/* 3. INFORMASI PEKERJA (MINIMAL FOOTER STRIP) */}
        {!requireDetailPermission &&
          (detailData.startDate ||
            detailData.dayOfBirth ||
            (!isWage && metricKey !== 'contract' && contractDocUrl)) && (
          <div className="mt-auto pt-3 border-top">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 text-muted" style={{ fontSize: '12px' }}>
              {detailData.startDate && (
                <div>
                  <span className="text-secondary">Mulai Bekerja: </span>
                  <span className="fw-medium text-dark">{formatIndonesianDate(detailData.startDate)}</span>
                </div>
              )}
              {detailData.dayOfBirth && metricKey !== 'minimumAge' && (
                <div>
                  <span className="text-secondary">Tanggal Lahir: </span>
                  <span className="fw-medium text-dark">{formatIndonesianDate(detailData.dayOfBirth)}</span>
                </div>
              )}
              {!isWage && metricKey !== 'contract' && contractDocUrl && (
                <div>
                  <a
                    href={contractDocUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary text-decoration-none d-inline-flex align-items-center gap-1"
                  >
                    <span>Dokumen Kontrak</span>
                    <ExternalLink size={11} />
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ComplianceDetailDrawer
